import React, { useMemo, useState, useEffect } from "react";
import { ListingHeader } from "sr/layout/common/ListingHeader";
import { Pagination } from "app/pages/component/Pagination";
import { useNavigate } from "react-router-dom";
import { Requirement, TabType } from "sr/models/Requirement";
import { getRequirements } from "sr/utils/api/corporate/requirements";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { StatusPill } from 'sr/helpers/ui-components/StatusPill'
import { MetaBox } from 'sr/helpers/ui-components/MetaBox'
import { getBusinessType } from 'sr/utils/api/masterData/getBusinessType'
import { serviceTypes as serviceTypeStatic } from '../requirement-post/RequirementJsonData'
import { useIntl } from 'react-intl'
import ContentWithLoader from 'sr/layout/common/ContentLoader'
import { getDuration } from 'sr/utils/api/masterData/getDuration'


export const RequirementListing = () => {
  const navigate = useNavigate();
  const intl = useIntl();

  const [tab, setTab] = useState<TabType>("Open");
  const [search, setSearch] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("-date");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [businessTypeOptions, setBusinessTypeOptions] = useState<{value:string; label:string}[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<{value:string; label:string}[]>([]);
  const [durationLabelMap, setDurationLabelMap] = useState<Record<string, string>>({});

  const corporateId = getUserDetails()?.user_id;

  const fetchData = async () => {
    if (!corporateId) return;
    setLoading(true);
    const res = await getRequirements({ corporate_id: corporateId, page, limit });
    if (res?.success) {
      setRequirements(res.data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [page, limit]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const bt = await getBusinessType();
        if (bt?.success && Array.isArray(bt.data)) {
          setBusinessTypeOptions(bt.data.map((i:any)=> ({ value:i.value, label:i.label })));
        }
      } catch {}
      setServiceTypeOptions(serviceTypeStatic.map((s:any)=> ({ value:s.value, label:s.label })));
      try {
        const d = await getDuration();
        if (d?.success && Array.isArray(d.data)) {
          const m: Record<string, string> = {};
          d.data.forEach((i: any) => (m[i.value] = i.label));
          setDurationLabelMap(m);
        }
      } catch {}
    };
    loadOptions();
  }, []);

  const filteredSorted = useMemo(() => {
    const filtered = requirements.filter((r) => {
      const inTab = tab === "Open" ? r.status === "open" : r.status === "closed";

      const q = (search || "").toLowerCase();
      const matches =
        r.title?.toLowerCase().includes(q) ||
        (Array.isArray((r as any).locations) &&
          (r as any).locations.some((loc: string) =>
            loc?.toLowerCase().includes(q)
          ));

      return inTab && matches;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "title") return a.title.localeCompare(b.title);
      if (sortBy === "-title") return b.title.localeCompare(a.title);
      if (sortBy === "date")
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      if (sortBy === "-date")
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      return 0;
    });
    return sorted;
  }, [requirements, tab, search, sortBy]);

  const { openCount, closedCount } = useMemo(() => {
    const open = requirements.filter((r) => r.status === 'open').length;
    const closed = requirements.filter((r) => r.status === 'closed').length;
    return { openCount: open, closedCount: closed };
  }, [requirements]);

  const totalPage = Math.max(1, Math.ceil(filteredSorted.length / limit));
  const start = (page - 1) * limit;
  const paged = filteredSorted.slice(start, start + limit);

  const formatAddress = (req: Requirement) => {
    if (!req.address) return "—";
    const { address_line_1, address_line_2, city, state, country, postal } =
      req.address;
    return [address_line_1, address_line_2, city, state, country, postal]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div className="py-3">
      <ListingHeader
        title={<span>Requirements</span>}
        tabs={[
          { key: 'Open', label: <>Open ({openCount})</> },
          { key: 'Closed', label: <>Closed ({closedCount})</> },
        ]}
        activeKey={tab}
        onTabChange={(k) => setTab(k as any)}
        search={search}
        onSearchChange={setSearch}
        sortBy={sortBy}
        onSortChange={setSortBy}
        primaryButton={(
          <button
            onClick={() => navigate('/corporate/requirement-post/basic')}
            className="flex items-center space-x-2 rounded-xl font-medium bg-[#ff39b3] px-4 py-2.5 text-white hover:bg-[#ff39b3] transition-colors"
          >
            Post Requirement
          </button>
        )}
        searchPlaceholderId="SEARCH.REQUIREMENT.PLACEHOLDER"
      />

      <ContentWithLoader
        loading={loading}
        content={
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="p-6 flex flex-col rounded-[20px] mb-6 border animate-pulse">
                <div className="flex justify-between items-center mb-3">
                  <div className="h-5 w-40 bg-gray-200 rounded" />
                  <div className="h-4 w-16 bg-gray-200 rounded-full" />
                </div>
                <div className="h-3 w-3/4 bg-gray-200 rounded mb-3" />
                <div className="flex gap-3">
                  <div className="h-10 w-40 bg-gray-200 rounded" />
                  <div className="h-10 w-28 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        }
      >
        <div className="mt-5 space-y-4">
          {paged.length > 0 ? (
            paged.map((req) => (
              <div
                key={req.id}
                onClick={() => navigate(`/corporate/requirement-listing/${req.id}`)}
                className="group p-6 flex flex-col md:flex-row gap-6 rounded-[20px] mb-6 border border-2xl transition cursor-pointer hover:bg-gray-50 hover:shadow"
              >
                <div className="flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-[#0078ae] group-hover:underline">
                        {req.title}
                      </h3>

                      {req?.address && (
                        <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                          {formatAddress(req)}
                        </span>
                      )}

                    </div>
                    <StatusPill status={req.status} />

                  </div>

                  <p className="text-sm text-gray-600 mb-4">{req.description}</p>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    <MetaBox label="Business Types" value={(req.business_types || []).map((v:any)=> {
                      const opt = businessTypeOptions.find(o=> o.value===v);
                      const lbl = opt?.label || v;
                      return intl.formatMessage({id: lbl, defaultMessage: lbl});
                    })} />
                    <MetaBox label="Services" value={(req.service_type || []).map((v:any)=> {
                      const opt = serviceTypeOptions.find(o=> o.value===v);
                      const lbl = opt?.label || v;
                      return intl.formatMessage({id: lbl, defaultMessage: lbl});
                    })} />
                    <MetaBox label="Duration" value={req.duration ? intl.formatMessage({ id: durationLabelMap[String(req.duration)] || String(req.duration), defaultMessage: String(req.duration) }) : "—"} />
                    <MetaBox
                      label="Start Date"
                      value={
                        req.start_date
                          ? new Date(req.start_date).toLocaleDateString()
                          : "—"
                      }
                    />
                  </div>

                  <div className="flex items-center text-xs text-gray-500 mt-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Updated {new Date(req.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-10 text-gray-500">
              {`No requirements in ${tab?.toLowerCase()} category.`}
            </div>
          )}
        </div>
        {filteredSorted.length > 0 && (
          <div className="mt-4">
            <Pagination page={page} totalPage={totalPage} setPage={setPage} />
          </div>
        )}
      </ContentWithLoader>
    </div>
  );
};
